import { cn } from '@/lib/cn';
import { XMarkIcon } from '@heroicons/react/16/solid';
import { useState, type JSX } from 'react';

import { resolveCurrentUserInFilterIds } from '@/requests/hooks/filter-rewrites';
import { Command } from 'cmdk';
import { CheckIcon } from 'lucide-react';
import { Button } from '../ui/button';
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  BOOLEAN_FILTER,
  DATE_FILTER,
  NULL_FILTER,
  NUMBER_FILTER,
  RELATION_FILTER,
  SELECT_FILTER,
  STRING_FILTER,
  TAG_FILTER,
  getReadableOperator,
  type AppliedFilter,
  type Filter,
  type FilterOperator,
  type FilterValue,
} from './constants';
import { FilterValueInput } from './filter-value-input';
import { RelationDisplay } from './relation-display';
import { RelationModal } from './relation-modal';

interface FilterItemProps {
  filterConfig: Filter;
  filter: AppliedFilter;
  updateFilterOperator: (operator: FilterOperator) => void;
  updateFilterValue: (value: FilterValue) => void;
  removeFilter: (operator: FilterOperator) => void;
  TableFilters: ({
    clientState,
    filters,
    tableId,
  }: {
    clientState?: boolean;
    filters: Filter[];
    tableId: string;
  }) => JSX.Element;
}

export function FilterItem({
  filterConfig,
  filter,
  updateFilterOperator,
  updateFilterValue,
  removeFilter,
  TableFilters,
}: FilterItemProps) {
  const [value, setValue] = useState<FilterValue>(filter.value);
  const [editValueOpen, setEditValueOpen] = useState(() => {
    if (filter.value === '' && filterConfig.kind !== BOOLEAN_FILTER) {
      return true;
    }
    return false;
  });
  const [editOperatorOpen, setEditOperatorOpen] = useState(false);
  const [relationSelectOpen, setRelationSelectOpen] = useState(false);

  const onOperatorChanged = (operator: FilterOperator) => {
    updateFilterOperator(operator);
    setEditOperatorOpen(false);
  };

  const onValueChanged = (val: FilterValue) => {
    updateFilterValue(val);
    setEditValueOpen(false);
    setRelationSelectOpen(false);
  };

  return (
    <>
      {relationSelectOpen &&
        (filterConfig.kind === RELATION_FILTER ||
          filterConfig.kind === TAG_FILTER) && (
          <RelationModal
            TableFilters={TableFilters}
            filter={filterConfig}
            initialValue={resolveCurrentUserInFilterIds(
              (value as string[]) ?? []
            )}
            onDone={(val) => {
              onValueChanged(val);
              setRelationSelectOpen(false);
            }}
          />
        )}

      <div className='flex flex-nowrap items-center'>
        <Button
          className='cursor-default rounded-l-full border bg-surface py-1.5 pr-2 pl-2.5 text-xs font-medium hover:bg-surface'
          variant='ghost'
        >
          {filterConfig.title}
        </Button>

        <Popover
          open={editOperatorOpen}
          onOpenChange={(open) => {
            setEditOperatorOpen(open);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              className='rounded-none border-y border-r border-r-border bg-surface px-2 py-1.5 text-xs text-muted-foreground'
              variant='ghost'
            >
              {getReadableOperator(filter.operator, filterConfig.kind)}
            </Button>
          </PopoverTrigger>
          <PopoverContent className='grid p-0'>
            <Command>
              <CommandInput className='h-9' placeholder={'Filtrera på...'} />
              <CommandList>
                <CommandEmpty>Inga resultat hittades.</CommandEmpty>
                <CommandGroup>
                  {filterConfig.operators.map((operator) => (
                    <CommandItem
                      className='justify-start text-start'
                      key={operator}
                      onSelect={() => {
                        onOperatorChanged(operator);
                      }}
                    >
                      <CheckIcon
                        className={cn(
                          'mr-2 size-4 opacity-0 transition-opacity',
                          filter.operator === operator && 'opacity-100'
                        )}
                      />
                      {getReadableOperator(operator, filterConfig.kind)}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {filterConfig.kind === NULL_FILTER ||
        filter.operator === '__isnull' ||
        filter.operator === '__isnull!' ? null : filterConfig.kind ===
            RELATION_FILTER || filterConfig.kind === TAG_FILTER ? (
          <>
            <Button
              onClick={() => setRelationSelectOpen(true)}
              variant='ghost'
              className='rounded-none border-y border-r border-r-border bg-surface px-2 py-1.5 text-xs font-medium'
            >
              <RelationDisplay filterConfig={filterConfig} filter={filter} />
            </Button>
          </>
        ) : (
          <Popover
            open={editValueOpen}
            onOpenChange={(open) => {
              if (!open && value != filter.value) {
                onValueChanged(value);
              }
              setEditValueOpen(open);
            }}
          >
            <PopoverTrigger asChild>
              <Button
                variant='ghost'
                className='rounded-none border-y border-r border-r-border bg-surface px-2 py-1.5 text-xs font-medium'
              >
                <div className='max-w-44 truncate'>
                  {filterConfig.kind === SELECT_FILTER &&
                  (Array.isArray(filter.value) || filter.value == null)
                    ? filter.value && filter.value?.length > 0
                      ? (filter.value || [])
                          .map(
                            (f) =>
                              filterConfig.choices?.find((c) => c.v == f)?.d
                          )
                          .join(', ')
                      : '...'
                    : filterConfig.kind === BOOLEAN_FILTER
                      ? (() => {
                          const v = filter.value;
                          const isFalse =
                            v === false ||
                            v === 'false' ||
                            (Array.isArray(v) &&
                              v.length === 1 &&
                              String(v[0]) === 'false');
                          return isFalse ? 'Falskt' : 'Sant';
                        })()
                      : filter.operator === '__date_past' ||
                          filter.operator === '__date_future' ||
                          filter.operator === '__date_time_past' ||
                          filter.operator === '__date_time_future'
                        ? typeof value === 'number' ||
                          (typeof value === 'string' && value !== '')
                          ? `${value} dagarna`
                          : '... dagarna'
                        : filter.value || '...'}
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className={cn(
                'p-0',
                (filterConfig.kind === STRING_FILTER ||
                  filterConfig.kind === NUMBER_FILTER ||
                  filterConfig.kind === DATE_FILTER) &&
                  'p-2'
              )}
            >
              <FilterValueInput
                onDone={() => onValueChanged(value)}
                filter={filterConfig}
                operator={filter.operator}
                value={value}
                setValue={(val) => {
                  setValue(val);
                }}
              />
            </PopoverContent>
          </Popover>
        )}
        <Button
          onClick={() => removeFilter(filter.operator)}
          variant='ghost'
          className='rounded-none rounded-r-full border-y border-r border-r-border bg-surface py-1.5 pr-2.5 pl-2 text-xs font-medium'
        >
          <XMarkIcon className='size-4' />
        </Button>
      </div>
    </>
  );
}
